import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { signup } from '../api/auth';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, UserPlus, Home } from 'lucide-react';
import toast from 'react-hot-toast';
import { validateEmail, validatePassword } from '../utils/validation';
import { sanitizeInput, sanitizeEmail } from '../utils/sanitizer';
import { sanitizeError } from '../utils/errorHandler';
import { logger } from '../utils/logger';

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors }, watch } = useForm();

  const password = watch('password');

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Input validation
      if (!validateEmail(data.email)) {
        toast.error('Geçerli bir e-posta adresi giriniz');
        return;
      }

      if (!validatePassword(data.password)) {
        toast.error('Şifre en az 8 karakter olmalı ve büyük harf, küçük harf, rakam içermelidir');
        return;
      }

      // Input sanitization
      const sanitizedData = {
        firstName: sanitizeInput(data.firstName),
        lastName: sanitizeInput(data.lastName),
        email: sanitizeEmail(data.email), // Email'i normalize et
        password: data.password
      };

      const r = await signup({
        email: sanitizedData.email,
        password: sanitizedData.password,
        data: { first_name: sanitizedData.firstName, last_name: sanitizedData.lastName }
      });
      if (!r.ok) {
        const error = await r.json().catch(() => ({}));
        // E-posta zaten kayıtlı ise daha anlaşılır mesaj göster
        const msg = (error?.message || error?.error_description || '').toLowerCase();
        if (msg.includes('already registered') || msg.includes('user already')) {
          toast.error('Bu e-posta ile daha önce kayıt olunmuş. Lütfen giriş yapın.');
          navigate('/login');
        } else {
          toast.error(error?.message || 'Kayıt başarısız');
        }
      } else {
        toast.success('Doğrulama e-postası adresinize gönderildi. Spam klasörünü de kontrol edin.');
        navigate('/login');
      }
    } catch (error) {
      logger.error('Kayıt hatası:', error);
      toast.error(sanitizeError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-32 left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-32 right-10 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl animate-bounce"></div>
        <div className="absolute top-1/3 right-1/3 w-72 h-72 bg-cyan-400/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-lg w-full space-y-8">

          {/* Glassmorphism Card */}
          <div className="backdrop-blur-xl bg-white/10 rounded-3xl p-8 shadow-2xl border border-white/20 relative">
            {/* Home Icon - Sağ Üst */}
            <Link
              to="/"
              className="absolute top-6 right-6 group p-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl hover:bg-white/30 hover:shadow-lg transform hover:scale-110 transition-all duration-300"
            >
              <Home className="h-5 w-5 text-white group-hover:text-white/90" />
            </Link>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl mb-4 shadow-lg">
                <UserPlus className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">
                Hesap Oluştur
              </h2>
              <p className="text-white/70">
                Hemen başlamak için bilgilerinizi girin
              </p>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-white/90 mb-2">
                    Ad
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-white/60 group-focus-within:text-white transition-colors" />
                    </div>
                    <input
                      {...register('firstName', {
                        required: 'Ad zorunludur',
                        minLength: {
                          value: 2,
                          message: 'Ad en az 2 karakter olmalıdır'
                        }
                      })}
                      type="text"
                      className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all duration-300"
                      placeholder="Adınız"
                    />
                  </div>
                  {errors.firstName && (
                    <p className="mt-2 text-sm text-red-300">{errors.firstName.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-white/90 mb-2">
                    Soyad
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-white/60 group-focus-within:text-white transition-colors" />
                    </div>
                    <input
                      {...register('lastName', {
                        required: 'Soyad zorunludur',
                        minLength: {
                          value: 2,
                          message: 'Soyad en az 2 karakter olmalıdır'
                        }
                      })}
                      type="text"
                      className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all duration-300"
                      placeholder="Soyadınız"
                    />
                  </div>
                  {errors.lastName && (
                    <p className="mt-2 text-sm text-red-300">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/90 mb-2">
                  E-posta adresi
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-white/60 group-focus-within:text-white transition-colors" />
                  </div>
                  <input
                    {...register('email', {
                      required: 'E-posta adresi zorunludur',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Geçerli bir e-posta adresi giriniz'
                      }
                    })}
                    type="email"
                    className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all duration-300"
                    placeholder="ornek@email.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-300">{errors.email.message}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white/90 mb-2">
                  Şifre
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-white/60 group-focus-within:text-white transition-colors" />
                  </div>
                  <input
                    {...register('password', {
                      required: 'Şifre zorunludur',
                      minLength: {
                        value: 8,
                        message: 'Şifre en az 8 karakter olmalıdır'
                      }
                    })}
                    type={showPassword ? 'text' : 'password'}
                    className="w-full pl-12 pr-12 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all duration-300"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/60 hover:text-white transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-300">{errors.password.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full group relative overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600 py-4 px-6 rounded-2xl text-white font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <span className="relative z-10 flex items-center justify-center">
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Hesap oluşturuluyor...
                    </>
                  ) : (
                    <>
                      Hesap Oluştur
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-teal-600 to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </form>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-white/20">
              <p className="text-center text-white/70">
                Zaten hesabınız var mı?{' '}
                <Link
                  to="/login"
                  className="text-white font-semibold hover:text-white/80 transition-colors"
                >
                  Giriş yapın
                </Link>
              </p>
            </div>
          </div>

          {/* Additional Info */}
          <div className="text-center">
            <p className="text-white/60 text-sm">
              Ücretsiz ve güvenli kayıt
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;